/*
 * Project: bellcloud
 * File: apps/frontend/src/api/core/axios.ts
 * Author: SWAN
 * Created: 2025-07-29 16:45:17
 * Description: axios 모듈화
 */
import axios, { AxiosHeaders, type AxiosResponse as AxiosResponseType } from 'axios';
import type { ApiSuccessResponse } from '@packages/types/common/dto';
import { apiConfig, logApiConfig, validateApiConfig, isPublicEndpoint } from './api.config';
import { triggerSessionTakeoverModal } from '@/store/sessionTakeoverStore';

/** Set on axios config after a successful token refresh + retry; prevents infinite 401 → refresh loops. */
const RETRY_AFTER_REFRESH_FLAG = '__retryAfterRefresh' as const;

/**
 * 비로그인 configuration에서 공개 수집 토큰을 붙일 엔드포인트.
 * 하드코딩 문자열 대신 api.config 단일 소스에서 파생한다.
 */
const PACKET_INGEST_PATH = apiConfig.endpoints.device?.packet ?? '/api/device/packet';

function isPacketIngestUrl(url: string | undefined): boolean {
  return !!url && url.includes(PACKET_INGEST_PATH);
}

// API 설정 로깅 및 검증
logApiConfig();
validateApiConfig();

const axiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// 요청 인터셉터 - JWT 토큰 자동 추가
axiosInstance.interceptors.request.use(
  config => {
    // 캐시 방지: 브라우저/Axios가 넣는 조건부 요청 헤더로 304 + 빈 바디가 오면
    // JSON 파싱이 깨질 수 있음. Axios 1.x는 AxiosHeaders라 delete 키만으로는 안 지워질 수 있음.
    if (config.method === 'get') {
      const headers = AxiosHeaders.from(config.headers ?? {});
      headers.delete('If-None-Match');
      headers.delete('if-none-match');
      headers.delete('If-Modified-Since');
      headers.delete('if-modified-since');
      headers.set('Cache-Control', 'no-cache');
      headers.set('Pragma', 'no-cache');
      config.headers = headers;
    }

    // 공개 엔드포인트는 토큰을 제외 (중앙 관리)
    if (config.url && isPublicEndpoint(config.url)) {
      return config;
    }

    // sessionStorage에서 토큰 확인
    const token = sessionStorage.getItem('access_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (isPacketIngestUrl(config.url)) {
      // 비로그인 configuration: 패킷 수집 엔드포인트 전용 공개 토큰 (VITE_* — 번들에 노출됨)
      const ingest = import.meta.env.VITE_WEB_CONFIGURATION_PACKET_INGEST_TOKEN as
        | string
        | undefined;
      if (ingest) {
        config.headers.Authorization = `Bearer ${ingest}`;
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 백엔드에서 이미 ApiSuccessResponse 구조로 응답
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  isRefreshing = false;
  failedQueue = [];
};

function isPublicAuthRoute(): boolean {
  if (typeof window === 'undefined') return true;
  const path = window.location.pathname;
  return (
    path.startsWith('/login') ||
    path.startsWith('/register') ||
    path.startsWith('/forgot-password') ||
    path.startsWith('/reset-password') ||
    path.startsWith('/verify-email') ||
    path.startsWith('/find-email')
  );
}

/** access token 수명이 응답에 없을 때 쓰는 보수적 기본값(초) — 서버 최소 수명과 동일 */
const ACCESS_TOKEN_FALLBACK_TTL_SECONDS = 2 * 60 * 60;

/**
 * access token 만료 시각(ISO)을 서버가 알려준 수명(expires_in, 초)으로 계산한다.
 * token_expiry는 "access token이 언제 만료되는가"만 나타내며,
 * 그 이후 세션 유지는 refresh token이 책임진다.
 */
export function accessTokenExpiryISO(expiresInSeconds?: number): string {
  const ttl =
    typeof expiresInSeconds === 'number' && expiresInSeconds > 0
      ? expiresInSeconds
      : ACCESS_TOKEN_FALLBACK_TTL_SECONDS;
  return new Date(Date.now() + ttl * 1000).toISOString();
}

export function clearAuthAndRedirect(): void {
  try {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token_expiry');
    delete axiosInstance.defaults.headers.common.Authorization;
    window.location.href = '/login';
  } catch {}
}

/** 로그인 상태로 앱 사용 중 세션이 끊긴 경우: 카운트다운 모달 후 로그인 이동 */
function notifyAuthLostThenLogin(): void {
  const hadSession =
    !!sessionStorage.getItem('access_token') || !!sessionStorage.getItem('refresh_token');
  if (!hadSession || isPublicAuthRoute()) {
    clearAuthAndRedirect();
    return;
  }
  triggerSessionTakeoverModal();
}

axiosInstance.interceptors.response.use(
  // 백엔드 ResponseInterceptor가 모든 성공 응답을 표준 envelope
  // ({ success, statusCode, message, data, pagination? })로 보장하므로
  // 여기서는 별도 정규화 없이 그대로 통과시킨다.
  (response: AxiosResponseType) => response,
  async error => {
    // 304 Not Modified는 정상적인 응답이므로 오류로 처리하지 않음
    if (error.response?.status === 304) {
      return Promise.resolve({
        data: error.response.data,
        status: 200,
        statusText: 'OK',
        headers: error.response.headers,
        config: error.config,
      });
    }

    const status = error.response?.status;
    const url = error.config?.url || '';

    // 로그인/리프레시 요청 자체의 실패는 그대로 전파
    if (status === 401 && (url.includes('/auth/login') || url.includes('/auth/refresh'))) {
      return Promise.reject(error);
    }

    // 401 에러: 토큰 만료 → refresh_token으로 갱신 후 재시도
    // axios 인터셉터에서 직접 처리 — 모든 API 호출에 투명하게 적용되어야 하므로 React Query 훅 불가.
    if (status === 401) {
      // Already retried once after refresh; another 401 is not "expired token" — stop or we loop forever.
      if ((error.config as Record<string, unknown>)?.[RETRY_AFTER_REFRESH_FLAG]) {
        notifyAuthLostThenLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (token) {
            error.config.headers.Authorization = `Bearer ${token}`;
            (error.config as Record<string, unknown>)[RETRY_AFTER_REFRESH_FLAG] = true;
            return axiosInstance(error.config);
          }
          return Promise.reject(error);
        });
      }

      isRefreshing = true;

      const refreshTokenValue = sessionStorage.getItem('refresh_token');

      if (!refreshTokenValue) {
        processQueue(new Error('No refresh token'), null);
        notifyAuthLostThenLogin();
        return Promise.reject(error);
      }

      return axiosInstance
        .post<
          ApiSuccessResponse<{
            access_token: string;
            refresh_token: string;
            expires_in?: number;
          }>
        >(apiConfig.endpoints.auth.refreshSession, { refresh_token: refreshTokenValue })
        .then(res => {
          const { access_token, refresh_token, expires_in } = res.data.data;
          sessionStorage.setItem('access_token', access_token);
          sessionStorage.setItem('refresh_token', refresh_token);
          // 갱신된 access token의 실제 수명을 반영 (rememberMe면 4시간, 아니면 2시간)
          sessionStorage.setItem('token_expiry', accessTokenExpiryISO(expires_in));
          axiosInstance.defaults.headers.common.Authorization = `Bearer ${access_token}`;
          processQueue(null, access_token);
          error.config.headers.Authorization = `Bearer ${access_token}`;
          (error.config as Record<string, unknown>)[RETRY_AFTER_REFRESH_FLAG] = true;
          return axiosInstance(error.config);
        })
        .catch(err => {
          processQueue(err, null);
          notifyAuthLostThenLogin();
          return Promise.reject(err);
        });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
